/**
 * Eén blok op het canvas: selecteerbaar, direct bewerkbaar, versleepbaar.
 *
 * Selection is deliberately kept outside the content as a subtle outline
 * with direct resize handles. The canvas never needs a floating design
 * toolbar over the content; detailed editing belongs in the Inspector.
 */
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

import { CanvasElement } from '../render/CanvasElement';
import { CanvasIcon } from '../render/CanvasIcon';

import { blockDefinition } from '../blocks/registry';
import type { SiteChrome, ContentBlock } from '../types';
import type { GridPlacement, ViewportKey } from '../types';
import { placementFor, placementStyle, setPlacement } from '../grid';
import { calculateAlignmentGuides } from '../guides';
import type { AlignmentGuide, GuideRect } from '../guides';
import { screenToCanvas } from '../canvasZoom';
import { placeToolbar, type ToolbarPosition } from '../toolbarPlacement';
import type { EditorInteractionAction, EditorInteractionState } from '../interaction';
import { InlineText } from './InlineText';

/** Elements whose primary content is a single run of text, edited in place.
 * Everything not listed here is composed of structured fields and is edited
 * through the inspector, so the canvas draws it read-only. */
const INLINE_EDITABLE = new Set<ContentBlock['type']>(['heading', 'paragraph', 'rich_text']);
type PlacementMode = 'move' | 'resize' | 'resize-left' | 'resize-top' | 'resize-height' | 'resize-corner' | 'resize-corner-nw' | 'resize-corner-ne' | 'resize-corner-sw';

interface CanvasBlockProps {
  block: ContentBlock;
  theme: SiteChrome['theme'];
  pathForPageId: (pageId: string) => string | null;
  index: number;
  total: number;
  selected: boolean;
  onSelect: () => void;
  onOpenInspector: () => void;
  onChangeText: (text: string) => void;
  onUpdateBlock: (patch: Partial<ContentBlock>) => void;
  onOpenMedia: () => void;
  onMove: (from: number, to: number) => void;
  onDuplicate: () => void;
  onDelete: () => void;
  /**
   * Sibling columns this element can move to, in visual order. Empty when the
   * section has only one column, in which case no control is shown -- an
   * always-visible "move to column" button that never has a destination is
   * just clutter.
   */
  columnTargets?: Array<{ columnId: string; label: string }>;
  onMoveToColumn?: (columnId: string) => void;
  viewport: ViewportKey;
  onPlacement: (patch: Partial<GridPlacement>) => void;
  onGuidesChange?: (guides: AlignmentGuide[]) => void;
  canvasScale: number;
  interaction: EditorInteractionState;
  onInteraction: (action: EditorInteractionAction) => void;
  /** The right inspector owns the controls while it is open. */
  inspectorOpen?: boolean;
  mediaUrl: (assetId: string) => string;
}

export function CanvasBlock({
  block,
  index,
  total,
  selected,
  onSelect,
  onOpenInspector,
  onChangeText,
  onUpdateBlock,
  onOpenMedia,
  onMove,
  theme,
  pathForPageId,
  onDuplicate,
  onDelete,
  columnTargets = [],
  onMoveToColumn,
  viewport,
  onPlacement,
  onGuidesChange,
  canvasScale,
  interaction,
  onInteraction,
  inspectorOpen = false,
  mediaUrl,
}: CanvasBlockProps) {
  const definition = blockDefinition(block.type);
  const placement = placementFor(block.layout, viewport);
  const legacyShapePlacement = block.type === 'shape' && block.layout?.desktop?.gridStart === 1 && block.layout.desktop.gridSpan === 6 && block.layout.desktop.height === 180 && block.layout.desktop.layer === 'normal';
  const baseVisualLayout = legacyShapePlacement && block.layout
    ? { ...block.layout, desktop: { ...block.layout.desktop, gridStart: 7, gridSpan: 5 } }
    : block.layout;
  const visualLayout = baseVisualLayout;
  // A shape is a real visual surface, not a line of content. Keep an explicit
  // height on both wrappers so percentage sizing inside the renderer cannot
  // collapse when its grid row has an automatic height.
  const [dragPreview, setDragPreview] = useState<Partial<GridPlacement> | null>(null);
  const previewLayout = dragPreview ? setPlacement(visualLayout, viewport, dragPreview) : visualLayout;
  const previewPlacement = dragPreview ? placementFor(previewLayout, viewport) : placement;
  const shapeVisualHeight = block.type === 'shape' ? previewPlacement.height ?? 180 : null;
  const shapeHeight = shapeVisualHeight == null ? undefined : `${shapeVisualHeight}px`;
  const isTextEditing = interaction.phase === 'TEXT_EDITING' && interaction.targetId === block.blockId;
  const isManipulating = (interaction.phase === 'DRAGGING_ELEMENT' || interaction.phase === 'RESIZING_ELEMENT') && interaction.targetId === block.blockId;
  const interactionCleanupRef = useRef<(() => void) | null>(null);
  const elementRef = useRef<HTMLDivElement | null>(null);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  const suppressClickAfterDragRef = useRef(false);
  const [elementToolbarPosition, setElementToolbarPosition] = useState<ToolbarPosition | null>(null);
  const elementToolbarRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => () => interactionCleanupRef.current?.(), []);
  useEffect(() => {
    if (!selected || isTextEditing || typeof document === 'undefined') {
      setElementToolbarPosition(null);
      return;
    }
    let frame = 0;
    const update = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(() => {
        frame = 0;
        const target = elementRef.current;
        const toolbar = elementToolbarRef.current;
        const surface = anchorRef.current?.closest<HTMLElement>('.zoea-canvas-surface');
        if (!target || !toolbar) return;
        const targetRect = target.getBoundingClientRect();
        const surfaceRect = surface?.getBoundingClientRect() ?? {
          left: 0,
          top: 0,
          right: window.innerWidth || 1280,
          bottom: window.innerHeight || 800,
          width: window.innerWidth || 1280,
          height: window.innerHeight || 800,
        };
        const toolbarRect = toolbar.getBoundingClientRect();
        setElementToolbarPosition(placeToolbar(targetRect, toolbarRect.width, toolbarRect.height, surfaceRect));
      });
    };
    const surface = anchorRef.current?.closest<HTMLElement>('.zoea-canvas-surface');
    surface?.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    const observers: ResizeObserver[] = [];
    if (typeof ResizeObserver !== 'undefined') {
      const targetObserver = new ResizeObserver(update);
      const toolbarObserver = new ResizeObserver(update);
      if (elementRef.current) targetObserver.observe(elementRef.current);
      if (elementToolbarRef.current) toolbarObserver.observe(elementToolbarRef.current);
      observers.push(targetObserver, toolbarObserver);
    }
    update();
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      surface?.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
      observers.forEach((observer) => observer.disconnect());
    };
  }, [selected, isTextEditing, canvasScale, viewport, dragPreview, interaction.phase]);
  const desktopPreviewPlacement = placementFor(previewLayout, 'desktop');
  const tabletPreviewPlacement = placementFor(previewLayout, 'tablet');
  const mobilePreviewPlacement = placementFor(previewLayout, 'mobile');
  const authoredAnchorHeight = (value: GridPlacement) => value.height == null ? 'auto' : `${value.height}px`;
  const shapeAnchorHeight = (value: GridPlacement) => value.height == null ? '180px' : `${value.height}px`;
  const anchorHeightFor = (value: GridPlacement) => block.type === 'shape' ? shapeAnchorHeight(value) : authoredAnchorHeight(value);
  const fontStacks: Record<string, string> = {
    'site-default': 'var(--zs-font-body)',
    inter: '"Inter", "Segoe UI", system-ui, sans-serif',
    manrope: '"Manrope", "Segoe UI", system-ui, sans-serif',
    'dm-sans': '"DM Sans", "Segoe UI", system-ui, sans-serif',
    'source-serif': '"Source Serif 4", Georgia, serif',
    'space-grotesk': '"Space Grotesk", "Segoe UI", system-ui, sans-serif',
  };
  const selectedFont = block.typography?.fontFamily;
  const typographyStyle: React.CSSProperties = {
    ...(selectedFont ? {
      '--zs-element-font-family': fontStacks[selectedFont] ?? fontStacks['site-default'],
    } as React.CSSProperties : {}),
    ...(block.typography?.fontSize ? { '--zs-element-font-size': `${block.typography.fontSize}px` } as React.CSSProperties : {}),
    ...(block.typography?.fontWeight ? { '--zs-element-font-weight': block.typography.fontWeight } as React.CSSProperties : {}),
    ...(block.typography?.lineHeight ? { '--zs-element-line-height': block.typography.lineHeight } as React.CSSProperties : {}),
    ...(block.typography?.letterSpacing != null ? { '--zs-element-letter-spacing': `${block.typography.letterSpacing}px` } as React.CSSProperties : {}),
    ...(block.typography?.textAlign ? { '--zs-element-text-align': block.typography.textAlign } as React.CSSProperties : {}),
    ...(block.typography?.color || block.typography?.customColor ? {
      '--zs-element-color': block.typography.customColor ?? `var(--zs-color-${block.typography.color})`,
    } as React.CSSProperties : {}),
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    // Alleen wanneer de omhulling zelf focus heeft (dus niet tijdens het
    // typen in de tekst) mag een toets het blok verwijderen of verplaatsen.
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
          onDelete();
    }
    if (event.key === 'ArrowUp' && (event.metaKey || event.altKey)) {
      event.preventDefault();
      onMove(index, index - 1);
    }
    if (event.key === 'ArrowDown' && (event.metaKey || event.altKey)) {
      event.preventDefault();
      onMove(index, index + 1);
    }
    if (selected && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      const amount = event.shiftKey ? 8 : 1;
      const horizontal = event.key === 'ArrowLeft' ? -amount : event.key === 'ArrowRight' ? amount : 0;
      const vertical = event.key === 'ArrowUp' ? -amount : event.key === 'ArrowDown' ? amount : 0;
      onPlacement({ offsetX: placement.offsetX + horizontal, offsetY: placement.offsetY + vertical });
    }
  };

  const beginPlacement = (event: React.PointerEvent, mode: PlacementMode, directDrag = false) => {
    // Direct element dragging is reserved for the explicit Move handle. A
    // content click remains selection-only so text, buttons and media keep
    // their native editing interactions.
    if (!directDrag) event.preventDefault();
    if (!directDrag) event.stopPropagation();
    // Keep ownership of the pointer after the cursor leaves the small handle.
    // Without capture, crossing another grid item could silently end the drag.
    const pointerTarget = event.currentTarget as HTMLElement;
    try { pointerTarget.setPointerCapture(event.pointerId); } catch { /* browser fallback */ }
    const resizeAxis = mode === 'resize-left' ? 'width-left'
      : mode === 'resize-top' ? 'height'
        : mode.startsWith('resize-corner') ? 'corner'
          : mode === 'resize-height' ? 'height' : 'width';
    const startX = event.clientX;
    const startY = event.clientY;
    const start = { ...placement };
    let latestX = startX;
    let latestY = startY;
    let manipulationStarted = !directDrag;
    if (manipulationStarted) {
      onInteraction(mode === 'move'
        ? { type: 'startDrag', targetId: block.blockId, viewport }
        : { type: 'startResize', targetId: block.blockId, viewport, axis: resizeAxis });
    }
    const blockElement = elementRef.current;
    const sectionElement = blockElement?.closest('.zs-section');
    const rectOf = (element: Element): GuideRect => {
      const rect = element.getBoundingClientRect();
      return { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom, width: rect.width, height: rect.height };
    };
    const initialBlockRect = blockElement ? rectOf(blockElement) : null;
    const sectionRect = sectionElement ? rectOf(sectionElement) : null;
    // Siblings are measured only as alignment/snap candidates. They are never
    // used to reject, clamp, push, or reflow this element's requested X/Y.
    const siblings = sectionElement
      ? Array.from(sectionElement.querySelectorAll<HTMLElement>('[data-testid^="sites-block-"]'))
        .filter((element) => element.dataset.testid !== `sites-block-${block.blockId}`)
        .map((element) => element.parentElement)
        .filter((element): element is HTMLElement => Boolean(element))
        .map(rectOf)
      : [];
    const columns = { desktop: 12, tablet: 8, mobile: 4 }[viewport];
    const gridLines = sectionRect ? Array.from({ length: columns + 1 }, (_, index) => sectionRect.left + sectionRect.width * index / columns) : [];
    // The canvas is zoomed and its usable width changes with the inspector.
    // A hard-coded 80px track made resize jump at 80%/fit zoom and whenever
    // the right panel changed the canvas width.
    const fallbackTrackSize = sectionRect ? sectionRect.width / Math.max(canvasScale, 0.01) / columns : 80;
    const startWidth = initialBlockRect ? initialBlockRect.width / Math.max(canvasScale, 0.01) : start.gridSpan * fallbackTrackSize;
    const startHeight = initialBlockRect ? initialBlockRect.height / Math.max(canvasScale, 0.01) : start.height ?? 160;
    const patchForDelta = (screenDx: number, screenDy: number): Partial<GridPlacement> => {
      const dx = screenToCanvas(screenDx, canvasScale);
      const dy = screenToCanvas(screenDy, canvasScale);
      if (mode === 'move') return { offsetX: start.offsetX + dx, offsetY: start.offsetY + dy };
      if (mode === 'resize-left') return { width: Math.max(40, startWidth - dx), offsetX: start.offsetX + dx };
      if (mode === 'resize-top') return { height: Math.max(40, Math.min(1200, startHeight - Math.round(dy))), offsetY: start.offsetY + Math.round(dy) };
      if (mode === 'resize-height') return { height: Math.max(40, Math.min(1200, startHeight + Math.round(dy))) };
      if (mode === 'resize-corner-nw') return { width: Math.max(40, startWidth - dx), height: Math.max(40, Math.min(1200, startHeight - Math.round(dy))), offsetX: start.offsetX + dx, offsetY: start.offsetY + Math.round(dy) };
      if (mode === 'resize-corner-ne') return { width: Math.max(40, startWidth + dx), height: Math.max(40, Math.min(1200, startHeight - Math.round(dy))), offsetY: start.offsetY + Math.round(dy) };
      if (mode === 'resize-corner-sw') return { width: Math.max(40, startWidth - dx), height: Math.max(40, Math.min(1200, startHeight + Math.round(dy))), offsetX: start.offsetX + dx };
      if (mode === 'resize') return { width: Math.max(40, startWidth + dx) };
      return { width: Math.max(40, startWidth + dx), height: Math.max(40, Math.min(1200, startHeight + Math.round(dy))) };
    };
    const alignmentGuidesFor = (screenDx: number, screenDy: number) => calculateAlignmentGuides({
      movingRect: initialBlockRect!,
      surfaceRect: sectionRect!,
      siblingRects: siblings,
      gridLines,
      delta: { x: screenDx, y: screenDy },
      scale: canvasScale,
    });
    if (mode === 'move' && sectionRect && initialBlockRect) onGuidesChange?.(alignmentGuidesFor(0, 0).guides);
    const move = (moveEvent: PointerEvent) => {
      latestX = moveEvent.clientX;
      latestY = moveEvent.clientY;
      let screenDx = latestX - startX;
      let screenDy = latestY - startY;
      if (directDrag && !manipulationStarted) {
        if (Math.hypot(screenDx, screenDy) < 4) return;
        manipulationStarted = true;
        suppressClickAfterDragRef.current = true;
        moveEvent.preventDefault();
        onInteraction({ type: 'startDrag', targetId: block.blockId, viewport });
      }
      if (mode === 'move' && sectionRect && initialBlockRect) {
        const result = alignmentGuidesFor(screenDx, screenDy);
        screenDx = result.dx;
        screenDy = result.dy;
        onGuidesChange?.(result.guides);
      }
      // Preview is local; the page model is committed once on release.
      setDragPreview(patchForDelta(screenDx, screenDy));
    };
    const finish = (commit: boolean) => {
      if (interactionCleanupRef.current === null) return;
      if (commit && manipulationStarted) {
        let screenDx = latestX - startX;
        let screenDy = latestY - startY;
        if (mode === 'move' && sectionRect && initialBlockRect) {
          const result = alignmentGuidesFor(screenDx, screenDy);
          screenDx = result.dx;
          screenDy = result.dy;
        }
        onPlacement(patchForDelta(screenDx, screenDy));
      }
      setDragPreview(null);
      onGuidesChange?.([]);
      if (manipulationStarted) onInteraction({ type: 'finishManipulation' });
      interactionCleanupRef.current?.();
      interactionCleanupRef.current = null;
    };
    const end = () => finish(true);
    const cancel = () => finish(false);
    pointerTarget.addEventListener('pointermove', move);
    pointerTarget.addEventListener('pointerup', end, { once: true });
    pointerTarget.addEventListener('pointercancel', cancel, { once: true });
    interactionCleanupRef.current = () => {
      pointerTarget.removeEventListener('pointermove', move);
      pointerTarget.removeEventListener('pointerup', end);
      pointerTarget.removeEventListener('pointercancel', cancel);
      try { pointerTarget.releasePointerCapture(event.pointerId); } catch { /* already released */ }
    };
  };

  return (
    <div
      ref={anchorRef}
      className={`zs-element-anchor zoea-element-anchor zs-element-type-${block.type}`}
      data-zoea-block-id={block.blockId}
      data-selected={selected ? 'true' : undefined}
      onClickCapture={(event) => {
        // A pointer drag may end on a nested renderer control (for example
        // the image picker button). Suppress the browser's follow-up click
        // before that control can interpret the drag as activation.
        if (suppressClickAfterDragRef.current) {
          suppressClickAfterDragRef.current = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        // Selection happens after the complete click gesture. Selecting on
        // pointerdown can close the active inspector while the pointer is
        // still down, changing Fit-mode canvas scale and making the document
        // appear to jump underneath the cursor.
        if (!selected) onSelect();
      }}
      onClick={(event) => {
        if (suppressClickAfterDragRef.current) {
          suppressClickAfterDragRef.current = false;
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        // A content click only selects. Inspector opening is explicit from
        // the editor toolbar and never competes with content interaction.
        event.stopPropagation();
      }}
      style={{
        ...placementStyle(previewLayout, viewport),
        ...typographyStyle,
        // These are the same authored/fallback anchor values used by the
        // public renderer. The Builder must not derive layout from a measured
        // DOM box: doing so changes section flow as soon as an element is
        // selected or its inspector opens.
        '--zs-anchor-height': anchorHeightFor(desktopPreviewPlacement),
        '--zs-anchor-floor': block.type === 'shape' ? '180px' : '96px',
        '--zs-anchor-tablet-height': anchorHeightFor(tabletPreviewPlacement),
        '--zs-anchor-mobile-height': anchorHeightFor(mobilePreviewPlacement),
        ...(shapeVisualHeight != null ? { '--zs-shape-height': `${shapeVisualHeight}px` } : {}),
        ...(block.motionDelay != null ? { '--zs-motion-delay': `${block.motionDelay}ms` } : {}),
        ...(block.motionDuration != null ? { '--zs-motion-duration': `${block.motionDuration}ms` } : {}),
      } as React.CSSProperties}
    >
    <div
      ref={elementRef}
      className={cn(
        'group relative zs-element',
        `zs-element-type-${block.type}`,
        theme.motion !== 'minimal' && block.motion && block.motion !== 'none' && `zs-element-motion-${block.motion}`,
        ...(block.customClasses ?? []),
        !selected && 'ring-1 ring-transparent hover:ring-slate-200',
        isTextEditing && 'zoea-text-editing',
        isManipulating && 'zoea-block-manipulating',
      )}
    >
      <div
        role="group"
        tabIndex={0}
        aria-label={`${definition.label}, blok ${index + 1} van ${total}`}
        data-testid={`sites-block-${block.blockId}`}
        onFocus={onSelect}
        onKeyDown={handleKeyDown}
        className={cn(
          // Editor interaction chrome must not become part of the canonical
          // website content box. Padding here changes wrapping and every
          // descendant's measured geometry compared with SiteRenderer.
          'rounded-xl transition-colors duration-150 outline-none',
          selected ? isTextEditing ? 'cursor-text' : 'cursor-default' : 'focus-visible:ring-1 focus-visible:ring-slate-300',
        )}
        style={shapeHeight ? { height: '100%', minHeight: '100%' } : undefined}
      >
        {/* Text-bearing elements stay directly editable on the canvas --
            typing into the page itself is the product's primary editing
            gesture. Everything else (buttons, cards, badges, lists) is drawn
            by the SAME renderer the published site uses, so the style
            controls in the inspector visibly do something. */}
        {INLINE_EDITABLE.has(block.type) ? (
          block.icon && block.icon !== 'none' ? (
            <div className={`zs-element-with-icon zs-element-with-icon-${block.iconPosition ?? 'top'}`}>
              {block.iconPosition !== 'right' ? <span className="zs-element-icon" aria-hidden="true"><CanvasIcon name={block.icon} /></span> : null}
              <InlineText
                value={block.text}
                onChange={onChangeText}
                placeholder={definition.placeholder}
                multiline={definition.multiline}
                as={block.type === 'heading' ? `h${block.level ?? 2}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' : definition.element}
                className={[definition.className, ...(block.customClasses ?? [])].filter(Boolean).join(' ')}
                editable={isTextEditing}
                ariaLabel={`${definition.label} bewerken`}
                onFocus={onSelect}
                onDoubleClick={() => onInteraction({ type: 'editText', targetId: block.blockId })}
                onBlur={() => onInteraction({ type: 'finishTextEdit' })}
                style={typographyStyle}
              />
              {block.iconPosition === 'right' ? <span className="zs-element-icon" aria-hidden="true"><CanvasIcon name={block.icon} /></span> : null}
            </div>
          ) : (
            <InlineText
              value={block.text}
              onChange={onChangeText}
              placeholder={definition.placeholder}
              multiline={definition.multiline}
              as={block.type === 'heading' ? `h${block.level ?? 2}` as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' : definition.element}
              className={[definition.className, ...(block.customClasses ?? [])].filter(Boolean).join(' ')}
              editable={isTextEditing}
              ariaLabel={`${definition.label} bewerken`}
                onFocus={onSelect}
                onDoubleClick={() => onInteraction({ type: 'editText', targetId: block.blockId })}
                onBlur={() => onInteraction({ type: 'finishTextEdit' })}
              style={typographyStyle}
            />
          )
        ) : (
          <CanvasElement block={block} theme={theme} pathForPageId={pathForPageId} mediaUrl={mediaUrl} editable={selected && (block.type === 'image' || block.type === 'gallery' || block.type === 'button' || block.type === 'shape')} textEditable={isTextEditing && (block.type === 'button' || block.type === 'shape')} onOpenMedia={onOpenMedia} onChangeText={(text) => onUpdateBlock({ label: text, text })} onStartTextEdit={() => onInteraction({ type: 'editText', targetId: block.blockId })} onFinishTextEdit={() => onInteraction({ type: 'finishTextEdit' })} style={typographyStyle} />
        )}
      </div>

      {/* Control layer: handles are siblings of the content, never content
          children. This keeps the visible bounding box exact and prevents
          resize buttons from appearing as detached green marks. */}
      {selected ? (
        <div
          className={cn('zoea-element-overlay', isManipulating && 'zoea-element-overlay-manipulating')}
          data-testid={`sites-block-overlay-${block.blockId}`}
          aria-label={`${definition.label} positionering`}
        >
          <button type="button" data-tooltip="Hoek" onPointerDown={(event) => beginPlacement(event, 'resize-corner-nw')} aria-label="Linkerbovenhoek: breedte en hoogte aanpassen" title="Breedte en hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-corner absolute left-0 top-0 z-20 cursor-nwse-resize" />
          <button type="button" data-tooltip="Hoogte" onPointerDown={(event) => beginPlacement(event, 'resize-top')} aria-label="Bovenrand: hoogte aanpassen" title="Hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-horizontal absolute left-1/2 top-0 z-20 cursor-ns-resize" />
          <button type="button" data-tooltip="Hoek" onPointerDown={(event) => beginPlacement(event, 'resize-corner-ne')} aria-label="Rechterbovenhoek: breedte en hoogte aanpassen" title="Breedte en hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-corner absolute right-0 top-0 z-20 cursor-nesw-resize" />
          <button type="button" data-tooltip="Breedte" onPointerDown={(event) => beginPlacement(event, 'resize-left')} aria-label="Linkerrand: breedte aanpassen" title="Breedte aanpassen" className="zoea-resize-handle zoea-resize-handle-vertical absolute left-0 top-1/2 z-20 cursor-ew-resize" />
          <button type="button" data-tooltip="Breedte" onPointerDown={(event) => beginPlacement(event, 'resize')} aria-label="Rechterrand: breedte aanpassen" title="Breedte aanpassen" className="zoea-resize-handle zoea-resize-handle-vertical absolute right-0 top-1/2 z-20 cursor-ew-resize" />
          <button type="button" data-tooltip="Hoek" onPointerDown={(event) => beginPlacement(event, 'resize-corner-sw')} aria-label="Linkeronderhoek: breedte en hoogte aanpassen" title="Breedte en hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-corner absolute bottom-0 left-0 z-20 cursor-nesw-resize" />
          <button type="button" data-tooltip="Hoogte" onPointerDown={(event) => beginPlacement(event, 'resize-height')} aria-label="Onderkant: hoogte aanpassen" title="Hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-horizontal absolute bottom-0 left-1/2 z-20 cursor-ns-resize" />
          <button type="button" data-tooltip="Hoek" onPointerDown={(event) => beginPlacement(event, 'resize-corner')} aria-label="Rechteronderhoek: breedte en hoogte aanpassen" title="Breedte en hoogte aanpassen" className="zoea-resize-handle zoea-resize-handle-corner absolute bottom-0 right-0 z-20 cursor-nwse-resize" />
        </div>
      ) : null}

      {selected && !isTextEditing && typeof document !== 'undefined' ? createPortal(
        <div
          ref={elementToolbarRef}
          className="zoea-element-toolbar fixed z-[200] flex items-center gap-1 rounded-xl border border-slate-200 bg-white p-1.5 text-xs shadow-xl"
          // Keep the portal in the accessibility tree while the first rAF
          // measurement is pending; it starts safely off-canvas and is moved
          // into place by the same scheduled measurement used for scroll.
          style={{ left: elementToolbarPosition?.left ?? -10000, top: elementToolbarPosition?.top ?? -10000, visibility: 'visible' }}
          role="toolbar"
          aria-label={`${definition.label} acties`}
          data-testid={`sites-element-toolbar-${block.blockId}`}
          onPointerDown={(event) => event.stopPropagation()}
        >
          <button type="button" className="cursor-grab rounded-lg bg-teal-600 px-2.5 py-1.5 font-semibold text-white hover:bg-teal-700 active:cursor-grabbing" onPointerDown={(event) => beginPlacement(event, 'move')} aria-label="Element verplaatsen">Verplaatsen</button>
          <button type="button" className="rounded-lg px-2.5 py-1.5 font-semibold text-slate-600 hover:bg-slate-100" onClick={onOpenInspector}>Ontwerp</button>
          <button type="button" className="rounded-lg px-2 py-1.5 font-semibold text-slate-600 hover:bg-slate-100" onClick={onDuplicate} aria-label="Element dupliceren">Kopie</button>
          <button type="button" className="rounded-lg px-2 py-1.5 font-semibold text-red-600 hover:bg-red-50" onClick={onDelete} aria-label="Element verwijderen">Verwijder</button>
        </div>,
        document.body,
      ) : null}

    </div>
    </div>
  );
}

function effectiveTextSize(block: ContentBlock, theme: SiteChrome['theme']): number {
  if (block.type === 'heading') {
    const scale = theme.typography.headingScale;
    const values = scale === 'sm' ? { 1: 48, 2: 32, 3: 24 } : scale === 'lg' ? { 1: 68, 2: 44, 3: 27 } : { 1: 58, 2: 38, 3: 25 };
    const level = (block.level ?? 2) as 1 | 2 | 3;
    return values[level] ?? values[2];
  }
  return theme.typography.bodyScale === 'lg' ? 19 : theme.typography.bodyScale === 'sm' ? 15 : 17;
}
